import { useId, type ComponentPropsWithoutRef, type JSX } from 'react';

import { IconSystem } from '#icons/IconSystem';
import type { IconVariantMap } from '#icons/icons-system';

import { Link } from '../Link/Link';

import styles from './TextButton.module.scss';

type ButtonType = {
	display: 'highlight' | 'default';
};

type PropsWithIcon<IconName extends keyof IconVariantMap> = {
	iconName: IconName;
	iconVariant: IconVariantMap[IconName];
} & ButtonType;

type PropsWithoutIcon = ComponentPropsWithoutRef<'button'> & ButtonType;

export function TextButton(props: ComponentPropsWithoutRef<'button'> & PropsWithoutIcon): JSX.Element;
export function TextButton<IconName extends keyof IconVariantMap>(
	props: ComponentPropsWithoutRef<'button'> & PropsWithIcon<IconName>
): JSX.Element;
export function TextButton<IconName extends keyof IconVariantMap>({
	className = '',
	children,
	display,
	id,
	...props
}: ComponentPropsWithoutRef<'button'> & (PropsWithIcon<IconName> | PropsWithoutIcon)) {
	const fallbackButtonId = useId();
	let icon = null;
	let rest: ComponentPropsWithoutRef<'button'>;
	if ('iconName' in props) {
		const { iconName, iconVariant, ...otherProps } = props;
		rest = otherProps;

		icon = <IconSystem icon={iconName} variant={iconVariant} aria-labelledby={id ?? fallbackButtonId} />;
	} else {
		rest = props;
	}

	return (
		<button
			className={`${className} ${styles.button} ${styles[display]} button-link no-focus no-hover`}
			id={id ?? fallbackButtonId}
			{...rest}
		>
			{icon}
			{children}
		</button>
	);
}

export function TextLinkButton(props: ComponentPropsWithoutRef<typeof Link> & PropsWithoutIcon): JSX.Element;
export function TextLinkButton<IconName extends keyof IconVariantMap>(
	props: ComponentPropsWithoutRef<typeof Link> & PropsWithIcon<IconName>
): JSX.Element;
export function TextLinkButton<IconName extends keyof IconVariantMap>({
	className = '',
	children,
	display,
	id,
	...props
}: ComponentPropsWithoutRef<typeof Link> & (PropsWithIcon<IconName> | PropsWithoutIcon)) {
	const fallbackButtonId = useId();
	let icon = null;
	let rest: ComponentPropsWithoutRef<typeof Link>;

	if ('iconName' in props) {
		const { iconName, iconVariant, ...otherProps } = props;
		rest = otherProps;

		icon = <IconSystem icon={iconName} variant={iconVariant} aria-labelledby={id ?? fallbackButtonId} />;
	} else {
		rest = props;
	}

	return (
		<Link className={`${className} ${styles.button} ${styles[display]} no-focus`} id={id ?? fallbackButtonId} {...rest}>
			{icon}
			{children}
		</Link>
	);
}
